<template>
  <div v-if="loading" class="page-loading">
    <Loading />
  </div>
  <div>
    <main class="profile-page">
      <div class="container">
        <div class="profile-card">
          <div class="user-info">
            <div class="avatar" v-if="user && user.name">
              <span>{{ user.name.charAt(0) }}</span>
            </div>
            <div class="user-details">
              <h1>{{ user.name }}</h1>
              <div class="email">
                <svg class="email-icon" viewBox="0 0 24 24">
                  <path
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                {{ user.email }}
              </div>
              <div class="join-date">Member since: {{ formattedDate }}</div>
            </div>
          </div>
        </div>

        <!-- Password Management Section -->
        <div class="profile-card">
          <span class="title-span">Change Password</span>

          <form @submit.prevent="handlePasswordChange" class="password-form">
            <div class="form-group">
              <label for="current-password">Current Password</label>
              <div class="password-input">
                <input
                  id="current-password"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  v-model="currentPassword"
                  required
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('current')"
                  class="toggle-password"
                >
                  {{ showCurrentPassword ? "👁️" : "🙈" }}
                </button>
              </div>
            </div>

            <div class="form-group">
              <label for="new-password">New Password</label>
              <div class="password-input">
                <input
                  id="new-password"
                  :type="showNewPassword ? 'text' : 'password'"
                  v-model="newPassword"
                  required
                  @focus="showRequirements = true"
                  @blur="showRequirements = false"
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('new')"
                  class="toggle-password"
                >
                  {{ showNewPassword ? "👁️" : "🙈" }}
                </button>
              </div>
              <password-strength-indicator
                :password="newPassword"
                :show-requirements="showRequirements"
              />
            </div>

            <div class="form-group">
              <label for="confirm-password">Confirm New Password</label>
              <div class="password-input">
                <input
                  id="confirm-password"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  v-model="confirmPassword"
                  required
                />
                <button
                  type="button"
                  @click="togglePasswordVisibility('confirm')"
                  class="toggle-password"
                >
                  {{ showConfirmPassword ? "👁️" : "🙈" }}
                </button>
              </div>
            </div>

            <div class="message-container">
              <p v-if="errorMessage" class="error-message">
                {{ errorMessage }}
              </p>
              <p v-if="successMessage" class="success-message">
                {{ successMessage }}
              </p>
            </div>

            <button
              type="submit"
              class="submit-button"
              :disabled="isLoading || !passwordsMatch"
            >
              <span v-if="isLoading" class="loading-spinner">
                <span class="spinner"></span>
                Updating...
              </span>
              <span v-else>Update Password</span>
            </button>
          </form>
        </div>
      </div>
    </main>
  </div>

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    :duration="2000"
    @closed="!notification.show"
  />
</template>

<script>
import { Me } from "@/api/user.api";
import Loading from "@/components/user/layout/LoadingComponent.vue";
import PasswordStrengthIndicator from "@/components/user/viewComponent/profile/PasswordStrengthIndicator.vue";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import { changePassword } from "@/api/auth.api";

export default {
  name: "ProfileComponent",
  components: {
    Loading,
    PasswordStrengthIndicator,
    NotificationComponent,
  },
  data() {
    return {
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      user: [],
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      isLoading: false,
      errorMessage: "",
      successMessage: "",
      showPassword: false,
      showRequirements: true,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      loading: false,
    };
  },
  async created() {
    await this.getMe();
  },
  computed: {
    formattedDate() {
      const localeTime = localStorage.getItem("localeTime") || "en-US";
      return new Date(this.user.created_at).toLocaleDateString(localeTime, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    passwordsMatch() {
      return this.newPassword === this.confirmPassword;
    },
  },
  methods: {
    async handlePasswordChange() {
      if (!this.passwordsMatch) {
        this.errorMessage = "New passwords do not match";
        return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const response = await changePassword(
          this.currentPassword,
          this.newPassword,
          this.confirmPassword
        );
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          this.currentPassword = "";
          this.newPassword = "";
          this.confirmPassword = "";
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async getMe() {
      this.loading = true;
      try {
        const response = await Me();
        if (response.data.status) {
          this.user = response.data.data;
        } else {
          this.showNotification("hold", "Error", response.data.msg);
        }
      } catch (error) {
        this.showNotification(
          "hold",
          "Error",
          error.response?.data?.msg || "Failed to fetch user data."
        );
      } finally {
        this.loading = false;
      }
    },
    togglePasswordVisibility(field) {
      if (field === "current") {
        this.showCurrentPassword = !this.showCurrentPassword;
      } else if (field === "new") {
        this.showNewPassword = !this.showNewPassword;
      } else if (field === "confirm") {
        this.showConfirmPassword = !this.showConfirmPassword;
      }
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
  },
};
</script>

<style scoped>
/* Layout */
.profile-page {
  padding: 25px;
}

.container {
  max-width: 768px;
  margin: 0 auto;
}

/* Cards */
.profile-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 24px;
}
.title-span {
  font-size: 18px;
  font-weight: bold;
}
/* User Information */
.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar {
  width: 64px;
  height: 64px;
  background-color: var(--hover-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar span {
  font-size: 24px;
  color: var(--logo-color);
  font-weight: bold;
}

.user-details h1 {
  font-size: 24px;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 4px;
}

.email {
  display: flex;
  align-items: center;
  color: #4b5563;
  margin-bottom: 4px;
}

.email-icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
}

.join-date {
  font-size: 14px;
  color: #6b7280;
}

/* Form Styles */
.password-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.password-input {
  position: relative;
}

.password-input input {
  width: 100%;
  padding: 8px 16px;
  padding-right: 40px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.password-input input:focus {
  outline: none;
  border-color: var(--logo-color);
  box-shadow: 0 0 0 3px rgba(199, 120, 2, 0.1);
}

.toggle-password {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #6b7280;
}

/* Messages */
.message-container {
  min-height: 24px;
}

.error-message {
  color: #dc2626;
  font-size: 14px;
}

.success-message {
  color: #16a34a;
  font-size: 14px;
}

/* Submit Button */
.submit-button {
  background-color: var(--logo-color);
  color: white;
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.submit-button:hover:not(:disabled) {
  background-color: var(--logo-hover-color);
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading Spinner */
.loading-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Design */
@media (max-width: 640px) {
  .profile-page {
    padding: 10px 0px;
  }

  .profile-card {
    margin-bottom: 10px;
    padding: 20px;
  }
  .user-info {
    flex-direction: column;
    text-align: center;
  }

  .user-details {
    align-items: center;
  }

  .email {
    justify-content: center;
    font-size: 12px;
  }

  .join-date{
    font-size: 12px;
  }

  .user-details h1 {
    font-size: 18px;
  }

  .title-span{
    font-size: 14px;
  }
  
  .form-group label {
    font-size: 12px;
  }

  .password-input input {
    font-size: 10px;
  }
  
  .toggle-password{
    font-size: 12px;
  }

  .password-form{
    margin-top: 10px;
    gap: 10px;
  }

  .submit-button{
    font-size: 12px;
  }
}
</style>
